// src/components/PurchaseFlow/Step/PaymentStep.js
import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import './PaymentStep.css';
import Skeleton from 'react-loading-skeleton';
import NavigationButtons from '../../NavigationButtons';
import 'react-loading-skeleton/dist/skeleton.css';
import api from '../../../../services/api';
import Swal from 'sweetalert2';
import { formatCurrency } from '../../../../utils/numberFormatter';
import { SelectedOfferContext } from '../../../../context/SelectedOfferContext';
import { trackSaleStepCompleted } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';

const PaymentStep = ({ onNext, onBack, clientData, purchaseRequestId, purchaseRequestUid, onCardTokenized }) => {
  const {
    selectedOffer,
    advancePaymentState,
    setAdvancePaymentState,
  } = useContext(SelectedOfferContext);
  const { commerceId, branchId } = useAnalyticsContext();
  const paymentWidgetRef = useRef(null);
  const pollingRunRef = useRef(0);
  const isMountedRef = useRef(true);

  const [isWidgetLoading, setIsWidgetLoading] = useState(true);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCardSaved, setIsCardSaved] = useState(false);
  const [suggestedCards, setSuggestedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showWidget, setShowWidget] = useState(false);
  const [isAddingNewCard, setIsAddingNewCard] = useState(false);
  const [advancePaymentMethod, setAdvancePaymentMethod] = useState(
    advancePaymentState?.method || null
  );

  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;
  const canSkipPaymentStep = process.env.REACT_APP_ENABLE_SKIP_PAYMENT_STEP === 'true';
  const forceDownpaymentDebitCard = process.env.REACT_APP_FORCE_DOWNPAYMENT_DEBIT_CARD === 'true';
  const forceDownpaymentAtEnd = process.env.REACT_APP_FORCE_DOWNPAYMENT_AT_END === 'true';
  const widgetDocument = clientData?.cuit || clientData?.dni || '';

  const enabledSections = process.env.REACT_APP_SUGGESTED_DATA_ENABLE?.split(',') || [];
  const showCardSuggestions = enabledSections.includes('card');
  const allowExpiredCards = process.env.REACT_APP_ALLOW_EXPIRED_CARDS === 'true';

  const isDownpaymentPlan = Boolean(selectedOffer?.isDownpaymentPlan);
  const shouldUseAdvancePaymentFlow =
    isDownpaymentPlan
    && (
      forceDownpaymentDebitCard
      || (advancePaymentMethod === 'debit_card' && !forceDownpaymentAtEnd)
    );
  const shouldShowAdvancePaymentChoice =
    isDownpaymentPlan
    && !forceDownpaymentDebitCard
    && !forceDownpaymentAtEnd
    && !advancePaymentMethod;
  const canUseCardSuggestions = showCardSuggestions && !shouldUseAdvancePaymentFlow;
  const canSkipCurrentStep = canSkipPaymentStep && !shouldUseAdvancePaymentFlow;
  const shouldLoadWidget =
    !shouldShowAdvancePaymentChoice &&
    (shouldUseAdvancePaymentFlow ||
      (showWidget && (!canUseCardSuggestions || suggestedCards.length === 0 || isAddingNewCard)));

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pollingRunRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!isDownpaymentPlan) {
      setAdvancePaymentMethod(null);
      return;
    }

    if (forceDownpaymentDebitCard) {
      setAdvancePaymentMethod('debit_card');
      setAdvancePaymentState((prevState) => ({
        ...prevState,
        method: 'debit_card',
      }));
      return;
    }

    if (forceDownpaymentAtEnd) {
      setAdvancePaymentMethod('other_method');
      setAdvancePaymentState((prevState) => ({
        ...prevState,
        method: 'other_method',
      }));
      return;
    }

    if (advancePaymentState?.method) {
      setAdvancePaymentMethod(advancePaymentState.method);
    }
  }, [
    isDownpaymentPlan,
    forceDownpaymentDebitCard,
    forceDownpaymentAtEnd,
    advancePaymentState?.method,
    setAdvancePaymentState,
  ]);

  const isCardExpiredBeforeLoan = (card) => {
    if (!selectedOffer?.term || !card?.months_until_expiration) return false;
    return card.months_until_expiration < selectedOffer.term;
  };

  const isCardHolderValid = (card) => {
    if (!clientData?.dni || !card?.holder_id) return false;
    return card.holder_id === clientData.dni;
  };

  const clearWidgetContainer = () => {
    const widgetContainer = document.getElementById('cirenioPaymentContainer');
    if (widgetContainer) {
      widgetContainer.innerHTML = '';
    }
  };

  const loadSuggestedCards = useCallback(async () => {
    if (!canUseCardSuggestions) {
      setIsLoadingSuggestions(false);
      return;
    }

    setIsLoadingSuggestions(true);

    try {
      const response = await api.get(`/customers/${clientData.id}/suggested-data/card`);
      const cards = response?.data?.data || [];

      setSuggestedCards(cards);

      if (cards.length === 0) {
        setShowWidget(true);
      }
    } catch (error) {
      console.error('Error loading suggested cards:', error);
      setShowWidget(true);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [canUseCardSuggestions, clientData?.id]);

  useEffect(() => {
    if (shouldUseAdvancePaymentFlow) {
      setShowWidget(true);
      setIsAddingNewCard(false);
      setIsLoadingSuggestions(false);
      return;
    }

    if (canUseCardSuggestions) {
      loadSuggestedCards();
    } else {
      setIsLoadingSuggestions(false);
      setShowWidget(true);
    }
  }, [canUseCardSuggestions, loadSuggestedCards, shouldUseAdvancePaymentFlow]);

  const updateAdvancePayment = useCallback((method, status = 'pending', payload = null) => {
    setAdvancePaymentMethod(method);
    setAdvancePaymentState({
      method,
      status,
      payload,
    });
  }, [setAdvancePaymentState]);

  const resetCardSelectionState = () => {
    clearWidgetContainer();
    setSelectedCardId(null);
    setIsCardSaved(false);
    setErrorMessage('');
    setIsWidgetLoading(true);
    setIsSavingCard(false);
    setReloadTrigger((prev) => prev + 1);
  };

  const handleAdvancePaymentChoice = (method) => {
    resetCardSelectionState();
    updateAdvancePayment(method);

    if (method === 'debit_card') {
      setSuggestedCards([]);
      setShowWidget(true);
      setIsAddingNewCard(false);
      setIsLoadingSuggestions(false);
      return;
    }

    setIsAddingNewCard(false);

    if (canUseCardSuggestions) {
      setIsLoadingSuggestions(true);
      loadSuggestedCards();
      return;
    }

    setShowWidget(true);
    setIsLoadingSuggestions(false);
  };

  const handleUseNewCard = () => {
    setSelectedCardId(null);
    setIsCardSaved(false);
    setShowWidget(true);
    setIsAddingNewCard(true);
    setErrorMessage('');
    setIsWidgetLoading(true);
    clearWidgetContainer();
    setReloadTrigger((prev) => prev + 1);
  };

  /**
   * Links an existing card to the purchase request.
   */
  const sendCardToApi = async (payload) => {
    setIsSavingCard(true);

    try {
      const response = await api.post(`/customers/${clientData.id}/cards`, payload);

      if (response.status === 201 || response.status === 200) {
        if (onCardTokenized) {
          onCardTokenized(response.data);
        }

        setIsCardSaved(true);
        setErrorMessage('');
        return response.data;
      }

      console.error('Unexpected response status:', response.status);
      setErrorMessage('Ocurrió un problema al guardar la tarjeta. Intentá de nuevo.');
      return null;
    } catch (error) {
      console.error('Error storing card token:', error);
      setErrorMessage('No pudimos guardar la tarjeta. Intentá de nuevo.');
      return null;
    } finally {
      setIsSavingCard(false);
    }
  };

  /**
   * Waits until the backend confirms the widget webhook.
   */
  const waitForWidgetWebhook = useCallback(async (runId) => {
    const maxAttempts = 12;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (!isMountedRef.current || runId !== pollingRunRef.current) {
        return false;
      }

      const response = await api.get(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/payment-status`
      );

      if (!isMountedRef.current || runId !== pollingRunRef.current) {
        return false;
      }

      const isReady = shouldUseAdvancePaymentFlow
        ? response.data?.downpayment?.ready
        : response.data?.card?.linked;

      if (isReady) {
        const card = response.data?.card || null;
        const downpayment = response.data?.downpayment || null;

        setIsCardSaved(true);
        setErrorMessage('');

        if (onCardTokenized) {
          onCardTokenized(card || response.data);
        }

        if (shouldUseAdvancePaymentFlow) {
          updateAdvancePayment('debit_card', 'paid', {
            card,
            payment_uid: downpayment?.payment_uid || null,
            payment_status: response.data,
          });
        } else if (isDownpaymentPlan) {
          updateAdvancePayment('other_method');
        }

        return response.data;
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    if (!isMountedRef.current || runId !== pollingRunRef.current) {
      return false;
    }

    setErrorMessage(
      shouldUseAdvancePaymentFlow
        ? 'Registramos el pago, pero todavía no lo confirmamos. Intentá de nuevo en unos segundos.'
        : 'Registramos la tarjeta, pero todavía no la confirmamos. Intentá de nuevo en unos segundos.'
    );
    return null;
  }, [
    clientData?.id,
    isDownpaymentPlan,
    onCardTokenized,
    purchaseRequestId,
    shouldUseAdvancePaymentFlow,
    updateAdvancePayment,
  ]);

  const saveWidgetResult = useCallback(async () => {
    setIsSavingCard(true);

    const currentRun = pollingRunRef.current;
    const paymentStatus = await waitForWidgetWebhook(currentRun);

    if (isMountedRef.current && currentRun === pollingRunRef.current) {
      setIsSavingCard(false);
    }

    return !!paymentStatus;
  }, [waitForWidgetWebhook]);

  const saveSuggestedCard = async (cardId) => {
    const payload = {
      purchase_request_id: purchaseRequestId,
      card_id: cardId,
    };

    const storedCard = await sendCardToApi(payload);

    if (!storedCard) {
      return false;
    }

    if (isDownpaymentPlan && !shouldUseAdvancePaymentFlow) {
      updateAdvancePayment('other_method');
    }

    return true;
  };

  const reloadWidget = useCallback(() => {
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    clearWidgetContainer();

    if (existingScript && document.body.contains(existingScript)) {
      document.body.removeChild(existingScript);
    }

    if (window.CirenioPayments) {
      delete window.CirenioPayments;
    }

    setErrorMessage('');
    setIsCardSaved(false);
    setIsWidgetLoading(true);
    setIsSavingCard(false);
    setReloadTrigger((prev) => prev + 1);
  }, [scriptSrc]);

  const handleWidgetSuccess = useCallback(async (cardData) => {
    const cardHolderId = cardData?.source?.holder?.identification;

    if (cardHolderId && clientData?.dni && cardHolderId !== clientData.dni) {
      await Swal.fire({
        html: `
          <div style="text-align: center; padding: 20px 10px;">
            <div style="margin-bottom: 30px; display: flex; justify-content: center; align-items: center;">
              <div style="position: relative; display: inline-block;">
                <i class="bi bi-credit-card" style="color: #6c757d; font-size: 80px; display: inline;"></i>
                <i class="bi bi-x-circle-fill" style="color: #dc3545; font-size: 20px; position: absolute; display: inline; top: 12px; right: -6px; background: white; border-radius: 50%;"></i>
              </div>
            </div>
            <h3 style="font-size: 22px; font-weight: 600; color: #333; margin-bottom: 20px;">
              La tarjeta debe estar a nombre del cliente
            </h3>
            <p style="font-size: 16px; margin-bottom: 15px; color: #555;">
              La tarjeta ingresada no pertenece a <strong>${clientData.firstName} ${clientData.lastName}</strong>.
            </p>
            <p style="font-size: 15px; color: #6c757d;">
              Pedile al cliente que use una tarjeta de débito a su nombre.
            </p>
          </div>
        `,
        confirmButtonText: 'Entendido, intentá de nuevo',
        confirmButtonColor: '#435ebe',
        allowOutsideClick: false,
        showCloseButton: false,
        customClass: {
          popup: 'swal2-popup-custom',
          confirmButton: 'btn btn-primary',
        },
      });

      reloadWidget();
      return;
    }

    await saveWidgetResult();
  }, [
    clientData?.dni,
    clientData?.firstName,
    clientData?.lastName,
    reloadWidget,
    saveWidgetResult,
  ]);

  useEffect(() => {
    if (!shouldLoadWidget) return undefined;

    const paymentsDocument = clientData?.cuit || clientData?.dni;
    const paymentDescription = 'Pago inicial con tarjeta de débito';
    const paymentConcept = selectedOffer?.productName || 'Pago inicial de la compra';

    if (!paymentsDocument || !purchaseRequestId) {
      setErrorMessage('Faltan datos para iniciar el pago con tarjeta.');
      return undefined;
    }

    pollingRunRef.current += 1;
    const currentRun = pollingRunRef.current;
    let isActive = true;
    let paymentsInstance = null;
    let script = null;

    clearWidgetContainer();
    setErrorMessage('');
    setIsWidgetLoading(true);

    const initializeWidget = () => {
      if (!isActive || currentRun !== pollingRunRef.current) return;

      if (!document.getElementById('cirenioPaymentContainer')) {
        return;
      }

      if (!window.CirenioPayments) {
        setIsWidgetLoading(false);
        setErrorMessage('No pudimos iniciar la pantalla de pago. Intentá de nuevo.');
        return;
      }

      const prSubjectKey = purchaseRequestUid || String(purchaseRequestId);
      const options = {
        containerId: 'cirenioPaymentContainer',
        externalId: `purchase_request:${prSubjectKey}`,
        subjectType: 'purchase_request',
        subjectId: String(purchaseRequestId),
        subjectUid: purchaseRequestUid || undefined,
        paymentContext: shouldUseAdvancePaymentFlow
          ? 'downpayment_debit_card'
          : 'tokenization_only',
      };

      if (shouldUseAdvancePaymentFlow) {
        Object.assign(options, {
          flowType: 'payment',
          advancePayment: {
            amount: Number(selectedOffer?.downpaymentAmount || 0),
          },
          paymentConcept,
          paymentDescription,
          firstName: clientData?.firstName,
          lastName: clientData?.lastName,
          email: clientData?.email,
          phone: clientData?.phone || clientData?.full_phone || undefined,
          showCloseButton: true,
        });
      }

      paymentsInstance = new window.CirenioPayments(
        publicKey,
        paymentsDocument,
        options
      );

      paymentsInstance.on('widget_start', () => {
        if (isActive) {
          setIsWidgetLoading(false);
        }
      });
      paymentsInstance.on('tokenization_success', async (cardData) => {
        if (!isActive || currentRun !== pollingRunRef.current) return;
        await handleWidgetSuccess(cardData);
      });
      paymentsInstance.on('tokenization_failure', (payload) => {
        if (isActive) {
          setErrorMessage(payload?.error || 'Hubo un problema con la tarjeta. Intentá de nuevo.');
        }
      });
      paymentsInstance.on('tokenization_error', () => {
        if (isActive) {
          setErrorMessage('Hubo un problema con la tarjeta. Intentá de nuevo.');
        }
      });

      paymentsInstance.openWidget();
    };

    if (window.CirenioPayments) {
      initializeWidget();
    } else {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = initializeWidget;
      script.onerror = () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setErrorMessage('No pudimos cargar la pantalla de pago. Revisá tu conexión e intentá de nuevo.');
        }
      };
      document.body.appendChild(script);
    }

    return () => {
      isActive = false;
      pollingRunRef.current += 1;
      if (paymentsInstance) {
        const cleanupMethod = paymentsInstance.destroy || paymentsInstance.close || paymentsInstance.unmount;
        if (cleanupMethod) {
          cleanupMethod.call(paymentsInstance);
        }
      }
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
      clearWidgetContainer();
    };
  }, [
    shouldLoadWidget,
    scriptSrc,
    publicKey,
    clientData,
    widgetDocument,
    purchaseRequestId,
    purchaseRequestUid,
    reloadTrigger,
    shouldUseAdvancePaymentFlow,
    handleWidgetSuccess,
    selectedOffer?.downpaymentAmount,
    selectedOffer?.productName,
  ]);

  const completeStep = () => {
    if (commerceId) {
      trackSaleStepCompleted({
        commerceId,
        branchId,
        stepName: 'PaymentStep',
        customerDni: clientData?.dni,
      });
    }

    onNext();
  };

  const handleContinue = async () => {
    if (selectedCardId) {
      const saved = await saveSuggestedCard(selectedCardId);
      if (saved) {
        completeStep();
      }
      return;
    }

    if (isCardSaved) {
      completeStep();
    }
  };

  const handleSkipStep = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'No se asociará ninguna tarjeta de débito a esta venta',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar sin tarjeta',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#435ebe',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-secondary',
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      if (isDownpaymentPlan && !shouldUseAdvancePaymentFlow) {
        updateAdvancePayment('other_method');
      }

      completeStep();
    });
  };

  const renderErrorAlert = () => {
    if (!errorMessage) return null;

    if (errorMessage.includes('No pudimos cargar la pantalla de pago')) {
      return (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>
          No pudimos cargar la pantalla de pago. Revisá tu conexión o{' '}
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ textDecoration: 'underline' }}
            onClick={reloadWidget}
          >
            intentá de nuevo
          </button>.
        </div>
      );
    }

    return (
      <div className="alert alert-warning text-center mt-3" role="alert">
        <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>
        {errorMessage}
      </div>
    );
  };

  const handleBackToSuggestions = () => {
    pollingRunRef.current += 1;
    setShowWidget(false);
    setIsAddingNewCard(false);
    setSelectedCardId(null);
    setIsCardSaved(false);
    clearWidgetContainer();
  };

  const handleSelectCard = (card) => {
    if (selectedCardId === card.id) {
      setSelectedCardId(null);
      return;
    }

    setSelectedCardId(card.id);
  };

  const renderAdvancePaymentChoiceModal = () => {
    if (!isDownpaymentPlan) return null;

    return (
      <div className="advance-choice-overlay" role="dialog" aria-modal="true" aria-labelledby="advance-choice-title">
        <div className="advance-choice-modal">
          <div className="text-center mb-4">
            <div className="advance-choice-icon">
              <i className="bi bi-cash-coin mb-4 me-3"></i>
            </div>
            <h2 id="advance-choice-title" className="mb-3">Elegí cómo cobrar el pago inicial</h2>
            <p className="text-muted mb-0">
              Hay que cobrar un pago inicial de <strong>{formatCurrency(selectedOffer?.downpaymentAmount || 0, 2)}</strong> para continuar.
            </p>
          </div>

          {forceDownpaymentDebitCard ? (
            <div className="alert alert-info border-0 shadow-sm mb-0 text-center" role="alert">
              En este comercio el pago inicial se cobra obligatoriamente con tarjeta de débito en este paso.
            </div>
          ) : (
            <div className="row g-3 mt-1">
              <div className="col-12 col-lg-6">
                <button
                  type="button"
                  className="advance-choice-card advance-choice-card--primary"
                  onClick={() => handleAdvancePaymentChoice('debit_card')}
                >
                  <span className="advance-choice-card__icon">
                    <i className="bi bi-credit-card-2-front mb-4 me-2"></i>
                  </span>
                  <span className="advance-choice-card__title">Pagar con tarjeta ahora</span>
                  <span className="advance-choice-card__description">
                    Cobrás el pago inicial ahora con tarjeta de débito.
                  </span>
                </button>
              </div>

              <div className="col-12 col-lg-6">
                <button
                  type="button"
                  className="advance-choice-card advance-choice-card--secondary"
                  onClick={() => handleAdvancePaymentChoice('other_method')}
                >
                  <span className="advance-choice-card__icon">
                    <i className="bi bi-receipt mb-4 me-2"></i>
                  </span>
                  <span className="advance-choice-card__title">Cobrar al final</span>
                  <span className="advance-choice-card__description">
                    Lo cobrarás en efectivo o transferencia.
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInfoCard = () => (
    <div className="col-5">
      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {shouldUseAdvancePaymentFlow ? 'Pago inicial con débito' : 'Información importante'}
          </h5>
          <div className="mb-4">
            <h6 className="mb-3">
              <i className="bi bi-credit-card me-2"></i>
              Requisitos de la tarjeta
            </h6>
            <ul className="list-unstyled">
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                Debe ser una tarjeta de débito
              </li>
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                Debe estar a nombre del cliente
              </li>
              <li className="mb-2">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                Debe estar vinculada a la cuenta donde recibe sus ingresos
              </li>
            </ul>
          </div>

          {shouldUseAdvancePaymentFlow && (
            <div className="mb-4">
              <h6 className="mb-3">
                <i className="bi bi-cash-coin me-2"></i>
                Pago inicial a cobrar ahora
              </h6>
              <p className="text-muted mb-0">
                Vas a debitar <strong>{formatCurrency(selectedOffer?.downpaymentAmount || 0, 2)}</strong> y, además, queda registrada la tarjeta para los cobros automáticos.
              </p>
            </div>
          )}

          {!shouldUseAdvancePaymentFlow && (
            <div className="mb-4">
              <h6 className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                ¿Para qué se utilizará?
              </h6>
              <p className="text-muted mb-2">
                La usamos como medio alternativo de cobro y queda vinculada para futuras compras del cliente.
              </p>
            </div>
          )}

          <div>
            <h6 className="mb-3">
              <i className="bi bi-shield-lock me-2"></i>
              Seguridad
            </h6>
            <p className="text-muted mb-0">
              Tus datos son tratados con los más altos estándares de seguridad bancaria y están protegidos por la ley de protección de datos personales.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWidget = () => (
    <div className="col-7">
      <div className="payment-widget-wrapper">
        {isWidgetLoading && shouldLoadWidget && <Skeleton height={680} />}
        <div
          id="cirenioPaymentContainer"
          ref={paymentWidgetRef}
          className="payment-widget"
          style={{ display: isWidgetLoading || !shouldLoadWidget ? 'none' : 'block' }}
        />
      </div>
    </div>
  );

  return (
    <div className="container my-5 payment-step-shell">
      <div className={shouldShowAdvancePaymentChoice ? 'payment-step-stage payment-step-stage--blurred' : 'payment-step-stage'}>
        <div className="position-relative mb-4">
          {!shouldUseAdvancePaymentFlow && isAddingNewCard && suggestedCards.length > 0 && (
            <button
              className="btn btn-link text-muted position-absolute start-0 top-50 translate-middle-y"
              onClick={handleBackToSuggestions}
              type="button"
            >
              <i className="bi bi-arrow-left me-1"></i>
              Volver a tarjetas guardadas
            </button>
          )}

          <h4 className="text-center mb-0">
            {shouldUseAdvancePaymentFlow ? 'Cobrá el pago inicial y vinculá la tarjeta' : 'Vinculá la tarjeta de débito'}
          </h4>

          {canSkipCurrentStep && (
            <button
              className="btn btn-link text-muted position-absolute end-0 top-50 translate-middle-y"
              onClick={handleSkipStep}
              type="button"
            >
              <i className="bi bi-arrow-right me-1"></i>
              Continuar sin tarjeta
            </button>
          )}
        </div>

        {shouldShowAdvancePaymentChoice ? null : (
          <div className="row justify-content-between align-items-start gx-4 mt-5">
            {canUseCardSuggestions && isLoadingSuggestions && (
              <div className="col-12 text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-2 text-muted">Cargando tarjetas existentes...</p>
              </div>
            )}

            {(!canUseCardSuggestions || !isLoadingSuggestions) && (
              <div className="row my-0 mt-4">
                {canUseCardSuggestions && suggestedCards.length > 0 && !isAddingNewCard ? (
                  <div className="col-12 suggested-data">
                    {isDownpaymentPlan && advancePaymentMethod === 'other_method' && (
                      <div className="alert alert-light border shadow-sm mb-4" role="alert">
                        El pago inicial queda pendiente para cobrar por otro medio en la confirmación. En este paso solo vinculamos la tarjeta para los débitos automáticos.
                      </div>
                    )}

                    <h6 className="mb-3 text-muted text-center">Seleccioná una tarjeta guardada o ingresá una nueva</h6>
                    <div className="row g-3">
                      {suggestedCards.map((card) => {
                        const isDeactivated = !!card.deactivated_at;
                        const expiredBeforeLoan = isCardExpiredBeforeLoan(card);
                        const holderValid = isCardHolderValid(card);
                        const isDisabled = isDeactivated || (expiredBeforeLoan && !allowExpiredCards) || !holderValid;
                        const warningClass = expiredBeforeLoan && allowExpiredCards ? ' warning-border' : '';
                        const cardColor = card.bank_color || '#6c757d';

                        return (
                          <div key={card.id} className="col-12 col-sm-6">
                            <div
                              className={`card-display list-only suggested-card simple${selectedCardId === card.id ? ' selected' : ''}${isDisabled ? ' disabled' : ''}${warningClass}`}
                              style={{ backgroundColor: isDisabled ? '#e0e0e0' : cardColor }}
                              onClick={!isDisabled ? () => handleSelectCard(card) : undefined}
                              title={
                                isDeactivated
                                  ? 'Esta tarjeta está desactivada'
                                  : !holderValid
                                    ? 'El titular de esta tarjeta no coincide con el DNI del cliente'
                                    : expiredBeforeLoan
                                      ? 'Esta tarjeta vence antes del último pago'
                                      : selectedCardId === card.id
                                        ? 'Deseleccionar esta tarjeta'
                                        : 'Seleccionar esta tarjeta'
                              }
                              aria-disabled={isDisabled}
                            >
                              <div className="card-header-row">
                                <div className="bank-info">
                                  {card.bank_logo ? (
                                    <div className="bank-logo-container">
                                      <img src={card.bank_logo} alt={card.bank_name || 'Banco'} />
                                    </div>
                                  ) : card.bank_name ? (
                                    <div className="bank-name">
                                      {card.bank_name}
                                    </div>
                                  ) : null}
                                </div>
                                <div className="card-last-four">
                                  {card.last_four}
                                </div>
                              </div>

                              <div className="card-content">
                                <div className="card-holder-info">
                                  <div className="card-holder">
                                    {card.holder_name}
                                  </div>
                                  <div className="card-expiry">
                                    {card.expiration_label}
                                  </div>
                                </div>

                                <div className="card-footer-row">
                                  {card.brand_logo ? (
                                    <div className="brand-logo-container">
                                      <img src={card.brand_logo} alt={card.brand_name} />
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: '8px', opacity: 0.8, maxWidth: '30px', textAlign: 'center' }}>
                                      {card.brand_name}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {isDeactivated && (
                                <span className="deactivated-label">
                                  Desactivada
                                </span>
                              )}

                              {!holderValid && !isDeactivated && (
                                <span className="deactivated-label">
                                  Titular no coincide
                                </span>
                              )}

                              {expiredBeforeLoan && !allowExpiredCards && !isDeactivated && holderValid && (
                                <span className="deactivated-label">
                                  Vence antes del último pago
                                </span>
                              )}

                              {expiredBeforeLoan && allowExpiredCards && holderValid && (
                                <span className="warning-icon">
                                  <i className="bi bi-exclamation-triangle-fill"></i>
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      <div className="col-12 col-sm-6">
                        <div
                          className="card-display list-only suggested-card simple d-flex align-items-center justify-content-center nueva-tarjeta"
                          onClick={handleUseNewCard}
                          tabIndex={0}
                          role="button"
                          title="Ingresá una tarjeta nueva"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') handleUseNewCard();
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                            <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>＋</span>
                            <span style={{ fontWeight: 500, fontSize: 15 }}>Ingresá una tarjeta nueva</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {renderInfoCard()}
                    {renderWidget()}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {renderErrorAlert()}

        {!shouldShowAdvancePaymentChoice && (
          <NavigationButtons
            onBack={onBack}
            onNext={handleContinue}
            disableNext={!selectedCardId && !isCardSaved}
            isLoading={isSavingCard}
            additionalButtons={
              canSkipCurrentStep ? (
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleSkipStep}
                  type="button"
                >
                  Omitir este paso
                </button>
              ) : null
            }
          />
        )}
      </div>

      {shouldShowAdvancePaymentChoice && renderAdvancePaymentChoiceModal()}
    </div>
  );
};

export default PaymentStep;

